import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';
import { Login } from './Login';
import { Shell, ModuleKey } from './Shell';
import { officeAgendas, officeModuleForLocation } from '../server/office-agendas';

const Finance = lazy(() => import('./Finance').then(module => ({ default: module.Finance })));
const Attendance = lazy(() => import('./Attendance').then(module => ({ default: module.Attendance })));
const Marketing = lazy(() => import('./Marketing').then(module => ({ default: module.Marketing })));
const Payroll = lazy(() => import('./Payroll').then(module => ({ default: module.Payroll })));
const Hr = lazy(() => import('./Hr').then(module => ({ default: module.Hr })));
const Procurement = lazy(() => import('./Procurement').then(module => ({ default: module.Procurement })));
const Wholesale = lazy(() => import('./Wholesale').then(module => ({ default: module.Wholesale })));
const EmployeeReports = lazy(() => import('./EmployeeReports').then(module => ({ default: module.EmployeeReports })));
const Marketplaces = lazy(() => import('./Marketplaces').then(module => ({ default: module.Marketplaces })));
const PriceAutomation = lazy(() => import('./PriceAutomation').then(module => ({ default: module.PriceAutomation })));

export interface User {
  id: string;
  identifier: string;
  permissions: string[];
  channels: Array<{ id: string; code: string }>;
}

function hostModule(): ModuleKey {
  return officeModuleForLocation(location.hostname, location.search);
}

function can(user: User, permissions: readonly string[]): boolean {
  return user.permissions.includes('SuperAdmin') || permissions.some(permission => user.permissions.includes(permission));
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const module = useMemo(hostModule, []);

  useEffect(() => {
    api<{ user: User }>('/api/auth/me')
      .then(result => setUser(result.user))
      .catch(error => {
        if (!(error instanceof ApiError) || error.status !== 401) setAuthError(error.message);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="boot"><div className="boot-mark">SHX</div><div className="spinner" /></div>;
  if (!user) return <Login error={authError} onLogin={setUser} />;

  const permitted = can(user, officeAgendas[module].permissions);

  return (
    <Shell user={user} activeModule={module} onLogout={() => setUser(null)}>
      {!permitted ? (
        <div className="empty-state panel">
          <span className="eyebrow">Přístup omezen</span>
          <h2>K této agendě nemáte oprávnění</h2>
          <p>Práva se spravují centrálně v rolích Vendure. Po jejich změně se znovu přihlaste.</p>
        </div>
      ) : <Suspense fallback={<div className="panel loading-panel"><div className="spinner" /><p>Načítám agendu…</p></div>}>
        {module === 'reports' ? <EmployeeReports /> : module === 'finance' ? <Finance user={user} /> : module === 'attendance' ? <Attendance user={user} /> : module === 'marketing' ? <Marketing /> : module === 'payroll' ? <Payroll user={user} /> : module === 'hr' ? <Hr user={user} /> : module === 'wholesale' ? <Wholesale /> : module === 'marketplaces' ? <Marketplaces /> : module === 'pricing' ? <PriceAutomation /> : <Procurement />}
      </Suspense>}
    </Shell>
  );
}
